<template>
    <div class="text-center" v-if="generating">
        <i class="icon-spinner6 spinner mr-2"></i>{{ $t('aiGeneration.loading.waitingForResponse') }}<span class="dots">.</span>
    </div>
    <template v-else>
        <div class="d-flex justify-content-end mb-2">
            <AppButton color="indigo" @click="generateDescription" :disabled="regenerating || (tokenBalance !== null && tokenBalance <= 0)">
                <i :class="['icon-sync mr-2', { 'spinner': regenerating }]"></i>
                {{ $t('aiGeneration.buttons.regenerate') }}
            </AppButton>
        </div>
        <div class="row">
            <div class="col-lg-6">
                <FormGroup :label="`${$t('labels.description')} (${ $t('aiGeneration.review.old') })`">
                    <InputTextArea :model-value="product.descriptions[mainIntegrationLanguage]" disabled/>
                </FormGroup>
            </div>
            <div class="col-lg-6">
                <FormGroup :label="`${$t('labels.description')} (${ $t('aiGeneration.review.new') })`">
                    <InputTextArea :model-value="generatedDescription" @input="$emit('update:generated-description', $event)"/>
                </FormGroup>
            </div>
        </div>
    </template>
</template>
<script>
import FormGroup from "../../../../../components/new/form/group/FormGroup.vue";
import InputTextArea from "../../../../../components/new/form/InputTextArea.vue";
import AppButton from "../../../../../components/new/button/AppButton.vue";

import GeminiService from "../../../../../services/GeminiService/GeminiService";
import MessageNotificator from '../../../../../services/MessageNotificator';
import { TooManyRequestsError } from '../../../../../services/Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../../../../../services/Errors/ServiceUnavailableError';
import { httpClient } from '../../../../../services/HttpClient';

export default {
    name: 'DescriptionScreen',
    components: {
        FormGroup, InputTextArea, AppButton
    },
    props: {
        generatedDescription: {
            type: String,
            default: ''
        },
        product: {
            type: Object,
            required: true
        },
        names: {
            type: String,
            default: null
        },
        seos: {
            type: String,
            default: null
        },
        geminiService: {
            type: GeminiService,
            required: true
        },
        mainIntegrationLanguage: {
            type: String,
            default: 'en'
        },
        integrationService: {
            type: String,
            default: null
        },
        formattedFilters: {
            type: Array,
            default: () => []
        },
        tokenBalance: {
            type: [Number, null],
            default: null
        }
    },
    emits: ['generatingStarted', 'generatingStopped', 'update:generated-description'],
    data() {
        return {
            generating: true,
            regenerating: false,
            prompts: {}
        }
    },
    methods: {
        async generateDescription() {
            this.regenerating = true;
            this.$emit('generatingStarted');
            const prompt = this.createPrompt();

            try {
                const response = await this.geminiService.generateString(prompt, {
                    temperature: 0.2,
                    topP: 0.95,
                    maxOutputTokens: 500
                });
                
                this.$emit('update:generated-description', response);
            } catch (error) {
                if (error instanceof TooManyRequestsError) {
                    new MessageNotificator().warning(this.$t('alerts.too_many_requests'));
                } else if (error instanceof ServiceUnavailableError) {
                    new MessageNotificator().error(this.$t('alerts.service_unavailable'));
                } else {
                    new MessageNotificator().error(this.$t('alerts.contact_support'));
                }
                return;
            } finally {
                await this.geminiService.flushTokens(this.$route.params.projectUuid, `Генерація опису для товару ${this.product.names[this.mainIntegrationLanguage] || Object.values(this.product.names)[0] || ''}`);
                this.$emit('tokensFlushed');
            }

            this.regenerating = false;
            this.generating = false;
            this.$emit('generatingStopped');
        },
        createPrompt() {
            let prompt = this.prompts[this.integrationService] || this.prompts.default;
            let currentProductDescriptions = Object.values(this.product.descriptions || {});

            let parametersForGeneration = `
                Product details:
                Name: ${this.names ? JSON.stringify(this.names) :  "Not specified"}
                Filters: ${JSON.stringify(this.formattedFilters)}
                Current description: ${currentProductDescriptions.length ? JSON.stringify(currentProductDescriptions[0]) : "Not specified"}
                SEO: ${this.seos ? JSON.stringify(this.seos) :  "Not specified"}

                Translation language: ${this.mainIntegrationLanguage}

                Return only the product description without any additional text or explanations.
            `;

            return prompt + parametersForGeneration;
        },
        async fetchPrompts() {
            const response = await httpClient.post(`/proxy/http-request`, {
                method: "GET",
                url: "https://fasunok.s3.eu-west-2.amazonaws.com/system/gemini-prompts/description.json"
            });

            if (response.status === 200) {
                this.prompts = response.data;
            }
        }
    },
    async mounted() {
        await this.fetchPrompts();
        await this.generateDescription();
    }
}
</script>